import React from 'react';

import { WeezSocket } from "@weezevent/piglet";

import StepHeader from './jo-components/misc/stepHeader/stepHeader';
import EventsFilter from './jo-components/misc/eventsFilter/eventsFilter';

import EventCard from './jo-components/cards/eventCard/eventCard';
import { hexToRgb }  from '../components/style';
import Color from '../utils/color';
import ToggleViewList from './toggleViewList/toggleViewList';
import { classnames } from '../utils/classnames';
import Icon from './jo-components/ui/icon/icon';
import '../style/components/multi.css';

function useWidgetEventStyles({ mainColor, backgroundColor }) {
	React.useEffect(() => {
		const root = document.documentElement;
		if (mainColor) {
			root.style.setProperty('--color-primary', mainColor);
			const mainColorRgb = hexToRgb(mainColor);
			const mainColorDarkRgb = hexToRgb(Color.fromString(mainColor).darken(50).toString());
			root.style.setProperty('--color-primary-rgba', mainColorRgb);
			root.style.setProperty('--color-primary-dark-rgba', mainColorDarkRgb);
		}
		if (backgroundColor) {
			root.style.setProperty('--background-color', backgroundColor);
		}
	}, [mainColor, backgroundColor]);
}

function WidgetEvent({ widgetKey, widgetEvent={}, lang, event, widget: { customs }={}, toggleList, useWidgetName = false}) {
    let endpoint = window.weezConfig.url.weezevent;
    let color = customs?.mainColor || '#0032FA';

	let urlParams = new URLSearchParams(window.location.search);
	const urlOrder = urlParams.get("o");
    let url = `${endpoint}/widget_billeterie.php?id_evenement=${event.id}&widget_key=${widgetKey}&locale=${lang}&color_primary=${color.replace('#', '')}&multiWidgetUrl=${encodeURIComponent(window.location.href)}`;

	if(urlOrder){
		url += `&o=${urlOrder}`
	}

    const redirectToWidget = React.useCallback(() => {
        // TODO to delete when widget filtered will be available in neo
        WeezSocket.send({ type:'filteredWidgetClick', origin:'neo', payload:{ id: event.id }});
        window.location.href = url;
    }, [ url, event ]);

    useWidgetEventStyles({
    	mainColor: customs.mainColor,
    	backgroundColor: customs.backgroundColor
    });

	const eventWithName = React.useMemo(() => {
		const regex = /^E\d+$/;

		const newEventName = useWidgetName || !regex.test(widgetKey) || !event.name
			? widgetEvent.name
			: event.name;

		return { ...event, name: newEventName };
	}, [useWidgetName, widgetKey, event, widgetEvent]);

	return <EventCard config={{event_header: eventWithName, settings: customs}} onClick={redirectToWidget} listMode={toggleList}/>
}

function EventWidgetGroup({ event, lang, widget, filters }) {
	const [showMore, setShowMore] = React.useState(true);
    const toggleShowMore = React.useCallback(() => setShowMore(showMore => !showMore), []);

	const widgets = React.useMemo(() => {
		// if filter on name, include all widgets if match with event name / otherwise only include matching widgets
		if (!filters?.search || (event.name.toLowerCase().includes(filters.search.toLowerCase())))
			return event.widgets;
		return event.widgets.filter(w => w.name.toLowerCase().includes(filters.search.toLowerCase()));
	}, [event, filters]);

	return (
		<div className='event-widget-group-container'>
			<div className='event-group-container'>
				<h3>
					<button
						type='button'
						id={`group_${event.id}`}
						aria-expanded={String(showMore)}
						aria-controls={`group_${event.id}_section`}
						className={classnames('neo-rate-group-card__trigger', {'neo-rate-group-card__trigger-closed': !showMore})}
						onClick={toggleShowMore}
					>
						<div className='neo-rate-group-card__trigger-content'>
							<div className='neo-rate-group-left'>
								<Icon name='calendar'/>
								<p className='neo-rate-group-title'>{event.name}</p>
							</div>
							<div className='neo-rate-group-right'>
								<div className='neo-rate-group-icon-container'>
									<Icon name='arrow' size='small' className={`neo-rate-group-icon ${showMore ? 'neo-rate-group-icon-down' : 'neo-rate-group-icon-up'}`} />
								</div>
							</div>
						</div>
					</button>
				</h3>
				<div
					role="region"
					id={`group_${event.id}_section`}
					aria-labelledby={`group_${event.id}`}
					className={classnames('event-group-content', {'display-block': showMore})}
				>
					{ widgets.map(we => (
						<WidgetEvent
							useWidgetName
							key={we.id}
							lang={lang}
							event={we.event}
							widget={widget}
							widgetEvent={we}
							widgetKey={we.widget_event_key}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function EventWidgetsList({ events, widget, lang, filters }) {
	const [toggleList, setToggleList] = React.useState(true);

	const groups = events.filter(e => e.widgets.length > 1);
	const not_grouped = events.filter(e => e.widgets.length === 1).map(e => e.widgets[0]);
	const full_list = events.map((event) => ( event.widgets[0] ));
	const is_filtered = Boolean(widget.first_step.slug === "multi");

	return (
		<>
			<ToggleViewList toggleList={toggleList} setToggleList={setToggleList}/>
			{(widget.customs?.display_group_modules && toggleList) ? (
				<>
					{
						groups.map(event => {
							return <EventWidgetGroup key={event.id} event={event} lang={lang} widget={widget} filters={filters} />
						})
					}
					{
						<div className='neo-widget-event-card-container'>
							{not_grouped.map(we => (
								<WidgetEvent key={we.id} event={we.event} lang={lang} widget={widget} widgetKey={we.widget_event_key} widgetEvent={we}/>
							))}
						</div>
					}
				</>
			) : (
				<div className={classnames('neo-event-widget-list', {'neo-event-widget-list-container': !toggleList})}>
					{
						is_filtered ? (
							widget.widget_events.map(we => (
								<WidgetEvent key={we.id} event={we.event} lang={lang} widget={widget} widgetKey={we.widget_event_key} widgetEvent={we} toggleList={toggleList}/>		
							))
						) : (
							full_list.map(we => (
								<WidgetEvent key={we.id} event={we.event} lang={lang} widget={widget} widgetKey={we.widget_event_key} toggleList={toggleList}/>
							))
						)
					}
				</div>
			)}
		</>
	)
}

export default function MultiEvent({ widget, lang, changeLanguage, isSubmitting }) {
	const [filters, setFilters] = React.useState(null);
	const widgetConfig = React.useMemo(() => {
		return {
			step_header: {
		  		available_languages: widget?.languages,
			}
		}
	}, [widget]);
	const { displayLanguageSelector = true } = widget?.customs;

	const events = React.useMemo(() => {
		const all_events = new Map();
		for (const we of widget.widget_events) { // eslint-disable-line no-unused-vars
			const event_id = we.event.id;
			const event = all_events.get(event_id) || {
				id: we.event.id,
				name: we.event.name,
				widgets: [],
				start_date: we.event.start_date,
				end_date: we.event.end_date
			};
			event.widgets.push(we);
			all_events.set(event_id, event);
		}
		return all_events;
	}, [widget]);

	const events_array = Array.from(events.values());
	const eventsFiltered = React.useMemo(() => events_array.filter(
		e => {
			let display = true;
			// we check that event's start and end are either before or after filters dates
			if (filters?.startDate && e.start_date)
				display = display && e.start_date >= filters.startDate;
			if (filters?.endDate && e.end_date)
				display = display && e.end_date <= filters.endDate;
			if (filters?.endDate && e.start_date)
				display = display && e.start_date <= filters.endDate;
			if (filters?.startDate && e.end_date)
				display = display && e.end_date >= filters.startDate;
			// search matches with event name and event's widgets names
			if (filters?.search)
				display = display &&
					(e.name.toLowerCase().includes(filters.search.toLowerCase()) ||
					e.widgets?.some(w => w.name.toLowerCase().includes(filters.search.toLowerCase())));
			return display;
		}
	), [filters]);

	return (
		<>
			{displayLanguageSelector && widget?.languages?.length > 0 && (
				<StepHeader
					config={widgetConfig}
					isSubmitting={isSubmitting}
					lang={lang}
					changeLanguage={changeLanguage}
					onSubmit={() => {}}
				/>
			)}
			<EventsFilter onChange={setFilters} />
			<EventWidgetsList events={eventsFiltered} widget={widget} lang={lang} filters={filters} />
		</>
	)
}
